import { motion } from 'framer-motion';
import { ArrowDownToLine, CalendarDays, MapPin } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/app/id6790435113';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.whatshappeningevents.whb_mobile';

const StoreBadge = ({ href, src, alt, testId, className }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="inline-flex rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-4 focus-visible:ring-offset-zinc-950"
  >
    <img src={src} alt={alt} className={className} />
  </a>
);

const AppDownloadSection = () => (
  <section
    data-testid="app-download-section"
    className="relative overflow-hidden border-y border-purple-500/20 bg-zinc-950 py-20 sm:py-24"
  >
    <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-purple-700/20 blur-3xl" />

    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 sm:px-12 lg:grid-cols-[1.2fr_0.8fr]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-500/10 px-4 py-2 text-sm font-semibold text-purple-300">
          <ArrowDownToLine className="h-4 w-4" aria-hidden="true" />
          Baltimore in your pocket
        </div>
        <h2 className="max-w-3xl font-outfit text-4xl font-bold tracking-tight text-white sm:text-5xl">
          Find your next favorite event <span className="text-purple-400">on the go.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-zinc-300">
          Download the What&apos;s Happening Baltimore app and discover the city&apos;s best arts,
          culture, music, and community events wherever you are.
        </p>

        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <StoreBadge
            href={APP_STORE_URL}
            src="/images/app-store-badge.svg"
            alt="Download on the App Store"
            testId="app-store-download"
            className="h-14 w-auto"
          />
          <StoreBadge
            href={GOOGLE_PLAY_URL}
            src="/images/google-play-badge.png"
            alt="Get it on Google Play"
            testId="google-play-download"
            className="h-[82px] w-auto -my-[13px]"
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="relative mx-auto w-full max-w-sm"
        aria-hidden="true"
      >
        <div className="absolute inset-8 rounded-full bg-purple-600/30 blur-3xl" />
        <div className="relative rotate-2 rounded-[2.5rem] border border-purple-400/30 bg-gradient-to-b from-purple-950 to-black p-3 shadow-2xl shadow-purple-950/70">
          <div className="rounded-[2rem] border border-white/10 bg-zinc-950 p-6">
            <div className="mx-auto mb-7 h-1.5 w-16 rounded-full bg-zinc-700" />
            <p className="text-sm font-semibold text-purple-300">What&apos;s happening?</p>
            <p className="mt-1 font-outfit text-2xl font-bold text-white">Explore Baltimore</p>
            <div className="mt-6 space-y-3">
              {[
                ['Live music tonight', 'Downtown Baltimore'],
                ['Weekend arts festival', 'Station North'],
                ['Community favorites', 'Near you'],
              ].map(([title, location], index) => (
                <div key={title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-purple-500/15 p-2 text-purple-300">
                      {index === 2 ? <MapPin className="h-5 w-5" /> : <CalendarDays className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{title}</p>
                      <p className="mt-1 text-sm text-zinc-400">{location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default AppDownloadSection;
